import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle, 
    DialogDescription 
} from '../components/ui/dialog';
import { 
    Music2, 
    BarChart3, 
    DollarSign, 
    Sparkles, 
    Database,
    TrendingUp,
    Globe,
    LineChart,
    Users,
    FileText,
    CreditCard,
    Eye,
    CheckCircle2,
    ArrowRight,
    Loader2
} from 'lucide-react';

const Homepage = () => {
    const [demoModalOpen, setDemoModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitDemo = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        // Simulate submission delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitting(false);
        setSubmitted(true);
    };

    const resetModal = () => {
        setDemoModalOpen(false);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', company: '', email: '', message: '' });
        }, 200);
    };

    const features = [
        {
            icon: <Database className="w-6 h-6" />,
            title: "Catalog Intelligence",
            description: "Manage releases, metadata, labels and rights in one unified platform."
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Performance Tracking",
            description: "Monitor streams, plays and growth across all platforms in real-time."
        },
        {
            icon: <DollarSign className="w-6 h-6" />,
            title: "Revenue Intelligence",
            description: "Track revenue, invoices and payments with complete transparency."
        },
        {
            icon: <Sparkles className="w-6 h-6" />,
            title: "AI Insights",
            description: "Identify trends, emerging markets and growth opportunities automatically."
        }
    ];

    const insights = [
        { icon: <TrendingUp className="w-5 h-5" />, text: "Discover trending catalog assets" },
        { icon: <Globe className="w-5 h-5" />, text: "Identify growing markets" },
        { icon: <LineChart className="w-5 h-5" />, text: "Forecast revenue opportunities" },
        { icon: <Users className="w-5 h-5" />, text: "Monitor label performance" }
    ];

    const transparencyFeatures = [
        { icon: <BarChart3 className="w-5 h-5" />, text: "Track catalog performance" },
        { icon: <DollarSign className="w-5 h-5" />, text: "View earnings in real-time" },
        { icon: <FileText className="w-5 h-5" />, text: "Raise invoices instantly" },
        { icon: <CreditCard className="w-5 h-5" />, text: "Monitor payment status" }
    ];

    return (
        <div className="min-h-screen bg-background overflow-x-hidden">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <img 
                                src="/branding/elara-logo.png" 
                                alt="ELARA" 
                                className="h-8 w-auto"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/admin/login">
                                <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                                    Login
                                </Button>
                            </Link>
                            <Button 
                                onClick={() => setDemoModalOpen(true)}
                                className="bg-primary hover:bg-primary/90 shadow-[0_0_15px_rgba(124,58,237,0.3)]"
                            >
                                Request Demo
                            </Button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
                {/* Background Gradient */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary/20 via-primary/5 to-transparent rounded-full blur-3xl" />
                    <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-3xl" />
                    <div className="absolute top-40 left-0 w-[300px] h-[300px] bg-neon-violet/10 rounded-full blur-3xl" />
                </div>
                
                {/* Grid Pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(124,58,237,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(124,58,237,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

                <div className="relative max-w-5xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-card/50 backdrop-blur-sm border border-border/50 rounded-full text-sm text-muted-foreground">
                        <Sparkles className="w-4 h-4 text-secondary" />
                        AI-Powered Music Business Intelligence
                    </div>
                    
                    <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                        Run your music business{' '}
                        <span className="gradient-text">on Elara</span>
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
                        AI-powered insights to manage catalogs, track performance, and grow music revenue — all in one unified platform.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button 
                            size="lg"
                            onClick={() => setDemoModalOpen(true)}
                            className="bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.4)] px-8 py-6 text-lg"
                        >
                            Request Demo
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                        <Link to="/admin/login">
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="border-border/50 hover:bg-card/50 px-8 py-6 text-lg"
                            >
                                Login to Dashboard
                            </Button>
                        </Link>
                    </div>

                    {/* Hero Visual */}
                    <div className="mt-16 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10 pointer-events-none" />
                        <div className="glass rounded-2xl p-1 shadow-2xl shadow-primary/20">
                            <div className="bg-card rounded-xl overflow-hidden border border-border/50">
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-muted/30">
                                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                    <span className="ml-2 text-xs text-muted-foreground">elara.app/dashboard</span>
                                </div>
                                <div className="p-6 sm:p-8">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                        <div className="bg-muted/30 rounded-lg p-4 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Total Revenue</div>
                                            <div className="text-xl sm:text-2xl font-bold text-foreground">₹24.5L</div>
                                            <div className="text-xs text-secondary mt-1">+12.5%</div>
                                        </div>
                                        <div className="bg-muted/30 rounded-lg p-4 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Active Songs</div>
                                            <div className="text-xl sm:text-2xl font-bold text-foreground">2,847</div>
                                            <div className="text-xs text-secondary mt-1">+48 new</div>
                                        </div>
                                        <div className="bg-muted/30 rounded-lg p-4 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Total Streams</div>
                                            <div className="text-xl sm:text-2xl font-bold text-foreground">89.2M</div>
                                            <div className="text-xs text-secondary mt-1">+8.3%</div>
                                        </div>
                                        <div className="bg-muted/30 rounded-lg p-4 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Labels</div>
                                            <div className="text-xl sm:text-2xl font-bold text-foreground">156</div>
                                            <div className="text-xs text-secondary mt-1">+3 new</div>
                                        </div>
                                    </div>
                                    <div className="mt-6 h-32 sm:h-40 bg-gradient-to-r from-primary/20 via-secondary/10 to-primary/5 rounded-lg flex items-end p-4">
                                        <div className="flex items-end gap-1 w-full">
                                            {[40, 65, 45, 80, 55, 70, 90, 60, 75, 85, 50, 95].map((height, i) => (
                                                <div 
                                                    key={i} 
                                                    className="flex-1 bg-gradient-to-t from-primary to-primary/50 rounded-t"
                                                    style={{ height: `${height}%` }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Problem Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-card/30 border-y border-border/30">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                        Music businesses run on <span className="text-primary">fragmented systems</span>
                    </h2>
                    <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
                        Catalog data lives in spreadsheets. Revenue reports come from multiple distributors. 
                        Analytics are scattered across platforms. Label settlements happen manually.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-12">
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                            <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                            <span className="text-sm text-muted-foreground">Scattered data</span>
                        </div>
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                            <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                            <span className="text-sm text-muted-foreground">Manual processes</span>
                        </div>
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                            <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                            <span className="text-sm text-muted-foreground">No visibility</span>
                        </div>
                    </div>
                    <div className="inline-flex items-center gap-3 p-4 rounded-xl bg-primary/10 border border-primary/20">
                        <CheckCircle2 className="w-6 h-6 text-secondary" />
                        <span className="text-foreground font-medium">Elara unifies everything in one intelligent platform</span>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                            Everything you need to run your music business
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            From catalog management to AI-powered insights, Elara gives you complete control over your music operations.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="group p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(124,58,237,0.15)]"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    {feature.icon}
                                </div>
                                <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Platform Insight Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-card/50 to-background">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 bg-secondary/10 border border-secondary/20 rounded-full text-sm text-secondary">
                                <Sparkles className="w-4 h-4" />
                                AI-Powered Analytics
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                                Turn streaming data into <span className="gradient-text">business intelligence</span>
                            </h2>
                            <p className="text-lg text-muted-foreground mb-8">
                                Elara's AI engine analyzes your catalog performance, identifies patterns, and surfaces actionable insights that help you make smarter decisions.
                            </p>
                            <div className="space-y-4">
                                {insights.map((insight, index) => (
                                    <div key={index} className="flex items-center gap-4 p-4 rounded-lg bg-card/50 border border-border/30">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                            {insight.icon}
                                        </div>
                                        <span className="text-foreground">{insight.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-3xl" />
                            <div className="relative glass rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium">AI Insight</div>
                                        <div className="text-xs text-muted-foreground">Just now</div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                                        <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
                                            <TrendingUp className="w-4 h-4" />
                                            Trending Alert
                                        </div>
                                        <p className="text-sm text-foreground">"Summer Nights" is gaining traction in Brazil (+340% streams this week)</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                                        <div className="flex items-center gap-2 text-primary text-sm font-medium mb-2">
                                            <Globe className="w-4 h-4" />
                                            Market Opportunity
                                        </div>
                                        <p className="text-sm text-foreground">Your indie catalog is performing 45% above average in Southeast Asia</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-neon-lime/10 border border-neon-lime/20">
                                        <div className="flex items-center gap-2 text-neon-lime text-sm font-medium mb-2">
                                            <LineChart className="w-4 h-4" />
                                            Revenue Forecast
                                        </div>
                                        <p className="text-sm text-foreground">Projected Q4 revenue: ₹18.2L based on current growth trajectory</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Transparency Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1 relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-primary/20 rounded-3xl blur-3xl" />
                            <div className="relative glass rounded-2xl overflow-hidden shadow-2xl">
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-muted/30">
                                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                    <span className="ml-2 text-xs text-muted-foreground">Label Dashboard</span>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <div className="text-sm text-muted-foreground">Welcome back,</div>
                                            <div className="text-xl font-semibold">Indie Records</div>
                                        </div>
                                        <div className="px-3 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-medium">
                                            Verified Partner
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Your Earnings</div>
                                            <div className="text-2xl font-bold">₹4.2L</div>
                                            <div className="text-xs text-secondary mt-1">This quarter</div>
                                        </div>
                                        <div className="p-4 rounded-lg bg-muted/30 border border-border/30">
                                            <div className="text-xs text-muted-foreground mb-1">Pending</div>
                                            <div className="text-2xl font-bold">₹82K</div>
                                            <div className="text-xs text-muted-foreground mt-1">Processing</div>
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-5 h-5 text-primary" />
                                                <div>
                                                    <div className="text-sm font-medium">Invoice #INV-2024-089</div>
                                                    <div className="text-xs text-muted-foreground">Submitted Jan 15</div>
                                                </div>
                                            </div>
                                            <span className="px-2 py-1 rounded bg-secondary/20 text-secondary text-xs">Approved</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 bg-primary/10 border border-primary/20 rounded-full text-sm text-primary">
                                <Eye className="w-4 h-4" />
                                Full Transparency
                            </div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                                Give your labels <span className="gradient-text">complete visibility</span>
                            </h2>
                            <p className="text-lg text-muted-foreground mb-8">
                                Elara provides dedicated dashboards for your label partners where they can track their catalog performance, view earnings, raise invoices, and monitor payment status in real-time.
                            </p>
                            <div className="space-y-4">
                                {transparencyFeatures.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-4 p-4 rounded-lg bg-card/50 border border-border/30">
                                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                                            {feature.icon}
                                        </div>
                                        <span className="text-foreground">{feature.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-b from-card/50 to-background" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(124,58,237,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(124,58,237,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/10 rounded-full blur-3xl" />
                
                <div className="relative max-w-3xl mx-auto text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                        Ready to run your music business{' '}
                        <span className="gradient-text">on Elara?</span>
                    </h2>
                    <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
                        Join leading music companies who trust Elara to manage their catalogs, track performance, and grow their revenue.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button 
                            size="lg"
                            onClick={() => setDemoModalOpen(true)}
                            className="bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.4)] px-8 py-6 text-lg"
                        >
                            Request Demo
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                        <Link to="/admin/login">
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="border-border/50 hover:bg-card/50 px-8 py-6 text-lg"
                            >
                                Login to Dashboard
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border/30">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <img 
                            src="/branding/elara-logo.png" 
                            alt="ELARA" 
                            className="h-6 w-auto opacity-60"
                        />
                    </div>
                    <div className="text-sm text-muted-foreground text-center">
                        © 2025 Elara – All Rights Reserved. Developed by{' '}
                        <a 
                            href="https://www.tricona.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Tricona
                        </a>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/admin/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                            Admin Login
                        </Link>
                        <Link to="/label/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                            Label Portal
                        </Link>
                    </div>
                </div>
            </footer>

            {/* Demo Request Modal */}
            <Dialog open={demoModalOpen} onOpenChange={resetModal}>
                <DialogContent className="sm:max-w-md bg-card border-border/50">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Request a Demo</DialogTitle>
                        <DialogDescription>
                            Fill out the form below and our team will get in touch with you shortly.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {submitted ? (
                        <div className="py-8 text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/20 flex items-center justify-center">
                                <CheckCircle2 className="w-8 h-8 text-secondary" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Thank you!</h3>
                            <p className="text-sm text-muted-foreground">
                                Your demo request has been submitted. We'll be in touch soon.
                            </p>
                            <Button 
                                className="mt-6"
                                onClick={resetModal}
                            >
                                Close
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitDemo} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    placeholder="Your name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="bg-background/50 border-border/50"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="company">Company</Label>
                                <Input
                                    id="company"
                                    name="company"
                                    placeholder="Your company name"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    required
                                    className="bg-background/50 border-border/50"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="you@company.com"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="bg-background/50 border-border/50"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="message">Message (optional)</Label>
                                <textarea
                                    id="message"
                                    name="message"
                                    placeholder="Tell us about your needs..."
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="flex w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 border-border/50 resize-none"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full bg-primary hover:bg-primary/90 shadow-[0_0_15px_rgba(124,58,237,0.3)]"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Request Demo'
                                )}
                            </Button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Homepage;
